import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './SubscriptionHistory.css';

const SubscriptionHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    entityType: '',
    subscriptionType: '',
    status: '',
    page: 1
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    currentPage: 1
  });

  useEffect(() => {
    fetchHistory();
  }, [filters]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.subscriptionType) params.append('subscriptionType', filters.subscriptionType);
      if (filters.status) params.append('status', filters.status);
      params.append('page', filters.page);
      params.append('limit', '50');

      const response = await axios.get(`${API_BASE_URL}/admin/subscription-history?${params}`);
      setHistory(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 1,
        currentPage: response.data.page || 1
      });
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch subscription history');
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
      page: 1 // Reset to first page when filter changes
    });
  };

  const handlePageChange = (newPage) => {
    setFilters({
      ...filters,
      page: newPage
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'expired':
        return 'status-badge status-expired';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge';
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && history.length === 0) {
    return (
      <Layout>
        <div className="subscription-history-container">
          <div className="history-header">
            <div className="history-header-content">
              <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '400px', height: '20px', borderRadius: '4px' }}></div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="history-filters">
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>

          {/* Stats Skeleton */}
          <div className="history-stats">
            {[1, 2, 3].map((index) => (
              <div key={index} className="stat-card">
                <div className="skeleton skeleton-text" style={{ width: '80px', height: '32px', borderRadius: '8px', margin: '0 auto 8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', margin: '0 auto' }}></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Subscription</th>
                  <th>Type</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Days Remaining</th>
                  <th>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="subscription-history-container">
        <div className="history-header">
          <div className="history-header-content">
            <h1>Subscription History</h1>
            <p className="history-subtitle">View all subscription activations and expirations</p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="history-filters">
          <div className="filter-group">
            <label>Entity Type</label>
            <select
              name="entityType"
              value={filters.entityType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="Hotel">Hotels</option>
              <option value="Spa">Spas</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Subscription Type</label>
            <select
              name="subscriptionType"
              value={filters.subscriptionType}
              onChange={handleFilterChange}
            >
              <option value="">All Subscriptions</option>
              <option value="verification">Verification</option>
              <option value="featured">Featured</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="history-stats">
          <div className="stat-card">
            <h3>{pagination.total}</h3>
            <p>Total Records</p>
          </div>
          <div className="stat-card">
            <h3>{history.filter(h => h.status === 'active').length}</h3>
            <p>Active Subscriptions</p>
          </div>
          <div className="stat-card">
            <h3>{history.filter(h => h.status === 'expired').length}</h3>
            <p>Expired</p>
          </div>
        </div>

        {/* History Table */}
        {history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h2>No subscription history found</h2>
            <p>Subscription history will appear here when subscriptions are activated.</p>
          </div>
        ) : (
          <>
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Subscription</th>
                    <th>Type</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Days Remaining</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => {
                    const daysRemaining = getDaysRemaining(entry.expiresAt);
                    return (
                      <tr key={entry._id}>
                        <td>
                          <div>
                            <strong>{entry.entityName}</strong>
                            <p className="table-meta">{entry.entityType}</p>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{entry.subscriptionName}</strong>
                            <p className="table-meta">
                              {entry.subscriptionDuration} {entry.subscriptionDurationUnit}
                            </p>
                          </div>
                        </td>
                        <td>
                          <span className={`type-badge type-${entry.subscriptionType}`}>
                            {entry.subscriptionType}
                          </span>
                        </td>
                        <td>{formatDate(entry.activatedAt)}</td>
                        <td>{formatDate(entry.expiresAt)}</td>
                        <td>
                          {daysRemaining !== null ? (
                            <span className={daysRemaining > 0 ? 'days-remaining' : 'days-expired'}>
                              {daysRemaining > 0 ? `${daysRemaining} days` : 'Expired'}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>
                          {entry.subscriptionCurrency === 'INR' ? '₹' : entry.subscriptionCurrency}{' '}
                          {entry.subscriptionPrice}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(entry.status)}>
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.currentPage} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.pages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default SubscriptionHistory;

